"use client"

import { motion } from "motion/react"
import { HomeIcon, CheckCircle } from "lucide-react"
import Link from "next/link"
import { useParams } from "next/navigation"

import { useServiceLanguage } from "@/core/hooks/use-service-language"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "@/core/ui/breadcrumb"
import { Button } from "@/core/ui/button"
import { JsonLd } from "@/core/ui/json-ld"
import { getServiceTheme } from "@/core/utils/service-theme"
import { serviceCategories } from "@/data/services"
import CategoryHero from "@/modules/marketing/services/components/category-hero"
import SubServiceCard from "@/modules/marketing/services/components/sub-service-card"
import TechGravity from "@/modules/marketing/services/components/tech-gravity"

export default function ServiceCategoryPage() {
  const params = useParams<{ category: string }>()
  const { getServiceData, getAllSubServicesForCategory } = useServiceLanguage()

  const serviceData = getServiceData(params.category as string)
  const subServices = getAllSubServicesForCategory(params.category as string)

  if (!serviceData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Button asChild variant="outline">
            <Link href="/services">Back to Services</Link>
          </Button>
        </div>
      </div>
    )
  }

  // Define relevant technologies for each category
  // In a real app, this should probably come from the data source
  const categoryTechMap: Record<string, string[]> = {
    blockchain: ["Solidity", "Rust", "Ethereum", "Smart Contracts", "Web3.js", "Hardhat", "IPFS", "DeFi"],
    genai: ["Python", "PyTorch", "TensorFlow", "OpenAI", "LangChain", "Hugging Face", "LLMs", "RAG"],
    development: ["React", "Next.js", "TypeScript", "Node.js", "PostgreSQL", "GraphQL", "AWS", "Docker"],
    enterprise: ["Kubernetes", "Microservices", "Java", "Spring Boot", "Kafka", "Redis", "Azure", "Terraform"]
  };

  // Default to all tech if category not found in map, or use the category specific list
  const activeTech = categoryTechMap[params.category as string]
    ? categoryTechMap[params.category as string]
    : ["React", "Next.js", "TypeScript", "Node.js", "Python", "AWS", "Docker", "Kubernetes"];

  const categoryBenefitsMap: Record<string, string[]> = {
    blockchain: ["Smart Contract Safety", "Decentralized Architecture", "Gas Optimization", "Cryptographic Security"],
    genai: ["Model Fine-tuning", "Context Optimization", "RAG Implementation", "Responsible AI"],
    development: ["Clean Code Principles", "Scalable System Design", "Performance Optimization", "CI/CD Automation"],
    enterprise: ["High Availability", "Data Compliance", "Legacy Integration", "Strategic Scalability"]
  };

  const activeBenefits = categoryBenefitsMap[params.category as string]
    ? categoryBenefitsMap[params.category as string]
    : ["Architecture-First Approach", "Security by Design", "Scalable Infrastructure", "Future-Proof Tech Stack"];

  // Niche-specific offers per category (for AI Commerce OfferCatalog)
  const categoryOffersMap: Record<string, Array<{ name: string; price: string; description: string }>> = {
    blockchain: [
      { name: "AI-Assisted Smart Contract Development", price: "5000", description: "Custom smart contracts with LLM-powered security guardrails" },
      { name: "DeFi Protocol Architecture", price: "15000", description: "End-to-end decentralized finance solutions" },
      { name: "Agentic Smart Contract Audits", price: "2500", description: "Comprehensive vulnerability assessment via AI agents" },
    ],
    genai: [
      { name: "Agentic Workflow Integration", price: "8000", description: "Custom AI agents integrated with enterprise tools" },
      { name: "RAG System Architecture", price: "5000", description: "Enterprise-grade retrieval-augmented generation" },
      { name: "LLM Fine-tuning & Deployment", price: "10000", description: "Custom model training and secure production deployment" },
      { name: "Multi-Agent System Orchestration", price: "15000", description: "CrewAI/LangGraph systems (e.g. Financial Data Gatherer + Quant Analyst + Report Writer agents)" },
    ],
    development: [
      { name: "AI-First MVP Development", price: "15000", description: "6-8 week intelligent application build from concept to production" },
      { name: "Agentic SaaS Platform Build", price: "30000", description: "Multi-tenant platform powered by internal AI workflows" },
      { name: "AI-Integrated Mobile App Development", price: "20000", description: "Cross-platform iOS and Android application with local LLMs" },
    ],
    enterprise: [
      { name: "AI Strategy & Advisory Retainer", price: "5000", description: "Monthly Principal-level guidance on AI integration" },
      { name: "Enterprise LLM Integration Assessment", price: "3500", description: "Investment-grade technical assessment for AI readiness" },
      { name: "Intelligent Architecture Review", price: "2500", description: "System design evaluation for agentic workflows" },
      { name: "Enterprise AI Governance & Guardrails", price: "12000", description: "Hallucination mitigation (e.g. Healthcare AI with semantic routers preventing PII leaks)" },
      { name: "Legacy API-to-Agent Bridging", price: "18000", description: "Connecting agents to CRMs (e.g. Support agent autonomously checking inventory and updating Salesforce)" },
    ],
  };

  const activeOffers = categoryOffersMap[params.category as string] || [];

  // Generate OfferCatalog schema for this category
  const categoryServiceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceData.title,
    "description": serviceData.description,
    "provider": {
      "@type": "Person",
      "name": "Hamid Ayub",
      "url": "https://hamidayub.com"
    },
    "areaServed": "Worldwide",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${serviceData.title} Packages`,
      "itemListElement": activeOffers.map((offer) => ({
        "@type": "Offer",
        "name": offer.name,
        "description": offer.description,
        "price": offer.price,
        "priceCurrency": "USD",
        "url": `https://hamidayub.com/contact?service=${offer.name.toLowerCase().replace(/\s+/g, '-')}`
      }))
    }
  };

  const theme = getServiceTheme(params.category as string)

  // Get related categories (excluding current)
  const relatedCategories = serviceCategories
    .filter((category) => category.id !== params.category)

  return (
    <div className={`min-h-screen bg-black text-white ${theme.selection}`}>
      <JsonLd data={categoryServiceJsonLd} />

      {/* Navigation Breadcrumb - Absolute positioned over hero */}
      <div className="absolute top-24 left-0 w-full z-20 px-4 pointer-events-none">
        <div className="container mx-auto pointer-events-auto">
          <Breadcrumb className="text-muted-foreground/40 hover:text-white/80 transition-colors inline-block backdrop-blur-xs px-4 py-2 rounded-full border border-white/5 bg-black/20">
            <BreadcrumbItem>
              <BreadcrumbLink href="/" className="hover:text-white transition-colors">
                <HomeIcon className="mr-2 h-3 w-3" />
                Home
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/services" className="hover:text-white transition-colors">Services</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href={`/services/${params.category}`} className={`${theme.breadcrumbActive} font-medium`}>
                {serviceData.title}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>
      </div>

      <CategoryHero
        title={serviceData.title}
        description={serviceData.description}
        icon={serviceData.icon}
        variant={params.category as string}
      />

      {/* Capabilities Grid */}
      <div id="capabilities" className="py-24 relative z-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <span className={`${theme.accentColor.replace('text-', 'text-')} font-mono text-sm tracking-widest uppercase mb-4 block`}>
                {theme.headerPrefix} {theme.sectionTitles.capabilities}
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                Available Modules
              </h2>
            </div>
            <p className="text-gray-400 max-w-md text-right hidden md:block">
              Select a module to initialize specific protocol sequences and implementation details.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subServices.map((service, index) => (
              <SubServiceCard
                key={service.id}
                index={index}
                id={service.id}
                categoryId={params.category as string}
                title={service.title}
                description={service.shortDescription}
                image={service.image}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Tech Gravity (Filtered) */}
      <div className="border-t border-white/5">
        <div className="container mx-auto px-4 pt-24 pb-12 text-center">
          <span className={`${theme.accentColor.replace('text-', 'text-')} font-mono text-sm tracking-widest uppercase mb-4 block`}>
            {theme.headerPrefix} {theme.sectionTitles.artifacts}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Domain Architecture
          </h2>
        </div>
        {/* We need to update TechGravity to accept props or we just render it here if it's flexible enough. 
            For now, let's assume we might need to update TechGravity or just let it show all. 
            Actually, the plan said "Reuse and adapt". I'll pass a filter prop if I edit it, 
            but since I didn't edit TechGravity to accept props yet, I should probably do that or make a new component.
            
            Wait, I haven't edited TechGravity yet. I'll read it again to be sure. 
            Ah, I read it before. It has a hardcoded list. 
            
            Let's update TechGravity to accept a 'customTags' prop.
        */}
        <TechGravity customTags={activeTech} theme={theme} />
      </div>

      {/* Why Partner / Methodology Section */}
      <div className="py-24 relative z-10 bg-white/2">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className={`${theme.accentColor.replace('text-', 'text-')} font-mono text-sm tracking-widest uppercase mb-4 block`}>
                {theme.headerPrefix} {theme.sectionTitles.protocol}
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
                Why This Domain?
              </h2>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                I don&apos;t just write code; I architect systems that scale. In the realm of {serviceData.title},
                precision and foresight are paramount.
              </p>

              <div className="space-y-6">
                {activeBenefits.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4 group"
                  >
                    <div className={`h-10 w-10 rounded-full ${theme.accentBg} border ${theme.accentBorder} flex items-center justify-center ${theme.accentColor} group-hover:${theme.accentBg.replace('/10', '/20')} group-hover:border-opacity-50 transition-all`}>
                      <CheckCircle className="h-5 w-5" />
                    </div>
                    <span className="text-lg text-gray-200 font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="relative h-[500px] rounded-3xl overflow-hidden border border-white/10 bg-black/50">
              <div className="absolute inset-0 bg-grid-white/[0.05] mask-[linear-gradient(to_bottom,transparent,black)]" />
              {/* Decorative Visualization */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`w-64 h-64 border ${theme.accentBorder} rounded-full animate-[spin_10s_linear_infinite]`} />
                <div className={`absolute w-48 h-48 border ${theme.accentBorder} rounded-full animate-[spin_15s_linear_infinite_reverse]`} />
                <div className={`absolute w-32 h-32 ${theme.accentBg} rounded-full blur-2xl animate-pulse`} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Other Categories Navigation */}
      <div className="py-24 border-t border-white/10">
        <div className="container mx-auto px-4">
          <h3 className="text-2xl font-bold mb-8 text-gray-400">Explore Other Domains</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {relatedCategories.map((category) => (
              <Link key={category.id} href={`/services/${category.id}`} className="group block">
                <div className={`p-6 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:${theme.accentBorder} transition-all text-center`}>
                  <span className={`block text-lg font-bold text-white group-hover:${theme.accentColor} transition-colors`}>
                    {category.translations.en.title}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

    </div>
  )
}
